import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  MapPin, Search, Calendar, Hotel, Plane, Star,
  ExternalLink, ChevronRight, Globe, Sun, Mountain, Car, Trophy, Flag,
  Sparkles, Loader2, Save, Share2, Trash2, Wand2, DollarSign, Users, Clock
} from 'lucide-react';
import { toast } from 'sonner';

// Featured Golf Destinations Data
const FEATURED_DESTINATIONS = [
  {
    id: 'myrtle-beach',
    name: 'Myrtle Beach',
    state: 'South Carolina',
    tagline: 'The Golf Capital of the World',
    description: 'With over 80 championship courses, Myrtle Beach offers incredible value and variety for golfers of all skill levels.',
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800&q=80',
    courses: ['Caledonia Golf & Fish Club', 'TPC Myrtle Beach', 'Barefoot Resort', 'Grande Dunes'],
    avgGreenFee: '$50-150',
    bestSeason: 'Spring & Fall',
    golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Myrtle+Beach',
    hotelsLink: 'https://www.google.com/travel/hotels/Myrtle%20Beach',
    color: 'from-blue-500 to-cyan-400'
  },
  {
    id: 'scottsdale',
    name: 'Scottsdale',
    state: 'Arizona',
    tagline: 'Desert Golf Paradise',
    description: 'Experience world-class desert golf with stunning mountain backdrops and year-round sunshine.',
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&q=80',
    courses: ['TPC Scottsdale', 'Troon North', 'Grayhawk Golf Club', 'We-Ko-Pa Golf Club'],
    avgGreenFee: '$100-300',
    bestSeason: 'Oct - April',
    golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Scottsdale+Arizona',
    hotelsLink: 'https://www.google.com/travel/hotels/Scottsdale%20Arizona',
    color: 'from-orange-500 to-amber-400'
  },
  {
    id: 'pebble-beach',
    name: 'Pebble Beach',
    state: 'California',
    tagline: 'The Ultimate Bucket List',
    description: 'Play where legends have made history. Pebble Beach offers the most iconic oceanfront golf experience in the world.',
    image: 'https://images.unsplash.com/photo-1600005082646-8a6f1f4f4e9f?w=800&q=80',
    courses: ['Pebble Beach Golf Links', 'Spyglass Hill', 'Spanish Bay', 'Cypress Point'],
    avgGreenFee: '$250-600',
    bestSeason: 'Year Round',
    golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Pebble+Beach',
    hotelsLink: 'https://www.google.com/travel/hotels/Pebble%20Beach%20California',
    color: 'from-emerald-500 to-teal-400'
  },
  {
    id: 'pinehurst',
    name: 'Pinehurst',
    state: 'North Carolina',
    tagline: 'The Cradle of American Golf',
    description: 'Home to 9 championship courses including the legendary No. 2, host of multiple U.S. Opens.',
    image: 'https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=800&q=80',
    courses: ['Pinehurst No. 2', 'Pinehurst No. 4', 'Pinehurst No. 8', 'Mid Pines'],
    avgGreenFee: '$150-400',
    bestSeason: 'Spring & Fall',
    golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Pinehurst+North+Carolina',
    hotelsLink: 'https://www.google.com/travel/hotels/Pinehurst%20North%20Carolina',
    color: 'from-green-600 to-emerald-500'
  }
];

// More destinations for search
const ALL_DESTINATIONS = [
  ...FEATURED_DESTINATIONS,
  { id: 'orlando', name: 'Orlando', state: 'Florida', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Orlando+Florida' },
  { id: 'las-vegas', name: 'Las Vegas', state: 'Nevada', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Las+Vegas+Nevada' },
  { id: 'san-diego', name: 'San Diego', state: 'California', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+San+Diego+California' },
  { id: 'hilton-head', name: 'Hilton Head', state: 'South Carolina', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Hilton+Head+South+Carolina' },
  { id: 'austin', name: 'Austin', state: 'Texas', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Austin+Texas' },
  { id: 'palm-springs', name: 'Palm Springs', state: 'California', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Palm+Springs+California' },
  { id: 'charleston', name: 'Charleston', state: 'South Carolina', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Charleston+South+Carolina' },
  { id: 'phoenix', name: 'Phoenix', state: 'Arizona', golfnowLink: 'https://www.google.com/search?q=book+golf+tee+times+Phoenix+Arizona' },
];

// Helper to open external links reliably in PWA
const openExternal = (url, e) => {
  if (e) e.preventDefault();
  window.open(url, '_blank', 'noopener,noreferrer');
};
const BUCKET_LIST_TRIPS = [
  {
    id: 'st-andrews',
    title: 'St Andrews — The Old Course',
    location: 'St Andrews, Scotland',
    why: 'The birthplace of golf. Walking the Swilcan Bridge is a rite of passage every golfer must experience.',
    estCost: '$5,000 - $8,000',
    bestTime: 'May - September',
    highlights: ['Old Course ballot entry', 'Swilcan Bridge', 'Links-style play', 'Golf history museums'],
    image: 'https://images.unsplash.com/photo-1591491719632-b89baaf8e17f?w=800&q=80',
    bookingUrl: 'https://www.standrews.com/play/the-old-course',
    gradient: 'from-sky-800 to-blue-600',
  },
  {
    id: 'augusta',
    title: 'Augusta National (Attend The Masters)',
    location: 'Augusta, Georgia',
    why: "You may never play it, but attending The Masters in person — Amen Corner, the azaleas, the roars — is pure golf magic.",
    estCost: '$2,500 - $6,000',
    bestTime: 'April (Masters Week)',
    highlights: ['Amen Corner', 'Pimento cheese sandwich', 'Patron experience', 'Magnolia Lane'],
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800&q=80',
    bookingUrl: 'https://www.masters.com/en_US/tickets/index.html',
    gradient: 'from-green-800 to-green-600',
  },
  {
    id: 'bandon-dunes',
    title: 'Bandon Dunes Golf Resort',
    location: 'Bandon, Oregon',
    why: "America's greatest golf resort. Five world-class courses on the rugged Oregon coast. Pure golf — no carts, no houses, no distractions.",
    estCost: '$3,000 - $5,000',
    bestTime: 'June - October',
    highlights: ['Bandon Dunes', 'Pacific Dunes', 'Old Macdonald', 'Sheep Ranch', 'Walking only'],
    image: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=800&q=80',
    bookingUrl: 'https://www.bandondunesgolf.com',
    gradient: 'from-emerald-800 to-teal-600',
  },
  {
    id: 'kiawah-island',
    title: 'Kiawah Island — The Ocean Course',
    location: 'Kiawah Island, South Carolina',
    why: 'Host of the 2021 PGA Championship. Every hole has ocean views, and the wind makes it one of the toughest tests in American golf.',
    estCost: '$2,000 - $4,000',
    bestTime: 'Spring & Fall',
    highlights: ['Ocean views every hole', 'PGA Championship host', 'Pete Dye design', 'Resort amenities'],
    image: 'https://images.unsplash.com/photo-1600005082646-8a6f1f4f4e9f?w=800&q=80',
    bookingUrl: 'https://www.kiawahresort.com/golf',
    gradient: 'from-cyan-800 to-blue-500',
  },
  {
    id: 'streamsong',
    title: 'Streamsong Resort',
    location: 'Bowling Green, Florida',
    why: "Hidden in central Florida, three top-100 courses built on former phosphate mines. Unlike anything else in the state — raw, rugged, unforgettable.",
    estCost: '$1,500 - $3,000',
    bestTime: 'October - April',
    highlights: ['Red, Blue & Black courses', 'All top-100 ranked', 'Remote getaway', 'No crowds'],
    image: 'https://images.unsplash.com/photo-1593111774240-d529f12cf4bb?w=800&q=80',
    bookingUrl: 'https://www.streamsongresort.com',
    gradient: 'from-amber-800 to-orange-600',
  },
  {
    id: 'cabo',
    title: 'Cabo San Lucas Golf Trip',
    location: 'Los Cabos, Mexico',
    why: 'Desert meets ocean on the Baja Peninsula. World-class Nicklaus and Dye courses with views that will make you forget about your scorecard.',
    estCost: '$2,500 - $5,000',
    bestTime: 'November - May',
    highlights: ['Cabo del Sol', 'Quivira Golf Club', 'Diamante', 'Ocean cliff holes'],
    image: 'https://images.unsplash.com/photo-1519046904884-53103b34b206?w=800&q=80',
    bookingUrl: 'https://www.visitloscabos.travel/things-to-do/golf',
    gradient: 'from-orange-700 to-yellow-500',
  },
];

export default function GolfTrips() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showMap, setShowMap] = useState(false);

  // AI Trip Planner state
  const [showPlanner, setShowPlanner] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [generatedTrip, setGeneratedTrip] = useState(null);
  const [savingTrip, setSavingTrip] = useState(false);
  const [myTrips, setMyTrips] = useState([]);
  const [viewingTrip, setViewingTrip] = useState(null);
  const [prefs, setPrefs] = useState({
    destination: '',
    duration_days: 4,
    group_size: 4,
    budget_per_person: '',
    skill_level: 'intermediate',
    travel_style: 'balanced',
    notes: '',
  });

  useEffect(() => {
    if (user) fetchMyTrips();
  }, [user]);

  // Scroll to the Book Tee Times section if we arrived with #book-tee-times in the URL
  useEffect(() => {
    if (window.location.hash === '#book-tee-times') {
      const t = setTimeout(() => {
        const el = document.getElementById('book-tee-times');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
      return () => clearTimeout(t);
    }
  }, []);

  const fetchMyTrips = async () => {
    try {
      const res = await fetch(`${API}/travel/my-trips`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setMyTrips(data.trips || []);
      }
    } catch (e) { /* ignore */ }
  };

  const handlePlanTrip = async () => {
    if (!prefs.destination.trim()) {
      toast.error('Where do you want to go?');
      return;
    }
    setPlanning(true);
    setGeneratedTrip(null);
    try {
      const payload = {
        ...prefs,
        budget_per_person: prefs.budget_per_person ? parseInt(prefs.budget_per_person) : null,
      };
      const res = await fetch(`${API}/travel/plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to generate trip');
        return;
      }
      const data = await res.json();
      setGeneratedTrip(data);
      toast.success('Your custom trip is ready!');
    } catch {
      toast.error('Network error');
    } finally {
      setPlanning(false);
    }
  };

  const handleSaveTrip = async () => {
    if (!generatedTrip) return;
    setSavingTrip(true);
    try {
      const res = await fetch(`${API}/travel/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ preferences: generatedTrip.preferences, itinerary: generatedTrip.itinerary }),
      });
      if (res.ok) {
        toast.success('Trip saved!');
        await fetchMyTrips();
      } else {
        toast.error('Failed to save trip');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSavingTrip(false);
    }
  };

  const handleShareTrip = async (trip) => {
    const url = `${window.location.origin}/golf-trips?trip=${trip.trip_id}`;
    try {
      if (navigator.share && /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent)) {
        await navigator.share({ title: trip.itinerary?.title || 'Golf Trip', url });
        return;
      }
    } catch { /* ignore */ }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Trip link copied');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const handleDeleteTrip = async (tripId) => {
    if (!window.confirm('Delete this trip?')) return;
    try {
      const res = await fetch(`${API}/travel/trip/${tripId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Trip deleted');
        fetchMyTrips();
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  const filteredDestinations = searchQuery 
    ? ALL_DESTINATIONS.filter(d => 
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.state.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const handleDestinationClick = (destination) => {
    setSelectedDestination(destination);
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      {/* Hero Section */}
      <div className="relative bg-bundle-green pt-16 md:pt-20 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-bundle-gold px-4 py-2 rounded-full mb-4">
              <Plane className="w-4 h-4 text-bundle-green-dark" />
              <span className="font-body text-sm font-semibold text-bundle-green-dark">Golf Travel</span>
            </div>
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4">
              Plan Your Golf Trip
            </h1>
            <p className="font-body text-lg text-white/80 max-w-2xl mx-auto">
              Discover amazing golf destinations, book tee times, and find the perfect hotels for your next golf getaway.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                data-testid="destination-search"
                placeholder="Search destinations (e.g., Scottsdale, Florida...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg bg-white border-0 shadow-xl rounded-xl"
              />
            </div>
            
            {/* Search Results Dropdown */}
            {searchQuery && filteredDestinations.length > 0 && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-64 overflow-y-auto">
                {filteredDestinations.map((dest) => (
                  <button
                    key={dest.id}
                    onClick={() => handleDestinationClick(dest)}
                    className="w-full px-4 py-3 text-left hover:bg-bundle-cream flex items-center gap-3 border-b border-gray-100 last:border-0"
                  >
                    <MapPin className="w-4 h-4 text-bundle-green" />
                    <div>
                      <p className="font-body font-medium text-bundle-green">{dest.name}</p>
                      <p className="font-body text-sm text-gray-500">{dest.state}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {searchQuery && filteredDestinations.length === 0 && (
              <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-xl border border-gray-100 z-50 p-4 text-center">
                <p className="font-body text-gray-500">No destinations found for "{searchQuery}"</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">

        {/* ===== AI TRIP PLANNER HERO ===== */}
        <div className="mb-12 relative overflow-hidden rounded-3xl bg-gradient-to-br from-bundle-green-dark via-bundle-green to-emerald-900 p-6 md:p-10 shadow-2xl" data-testid="ai-trip-planner">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-bundle-gold/20 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-bundle-gold/10 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-bundle-gold/20 border border-bundle-gold/40 mb-4">
                <Sparkles className="w-3.5 h-3.5 text-bundle-gold" />
                <span className="font-body text-[11px] uppercase tracking-widest text-bundle-gold font-bold">AI Concierge</span>
              </div>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white leading-tight mb-3">
                Plan your dream<br />golf trip in 30 seconds
              </h2>
              <p className="font-body text-white/80 text-base md:text-lg mb-6 max-w-md">
                Tell us where you want to go, your budget, and your group. We'll draft a custom day-by-day itinerary with real courses, hotels, and pro tips.
              </p>
              <Button
                onClick={() => { setShowPlanner(true); setGeneratedTrip(null); }}
                data-testid="open-trip-planner-btn"
                className="bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold text-base px-6 py-6 rounded-full shadow-xl"
              >
                <Wand2 className="w-5 h-5 mr-2" />
                Plan My Trip with AI
              </Button>
            </div>
            <div className="hidden md:block relative">
              <div className="absolute inset-0 bg-bundle-gold/10 blur-2xl rounded-full" />
              <div className="relative space-y-3">
                {[
                  { icon: MapPin, label: 'Real course recommendations', sub: 'Pulled from a global golf database' },
                  { icon: Hotel, label: 'Hotel & lodging suggestions', sub: 'Match your budget & style' },
                  { icon: Calendar, label: 'Day-by-day itinerary', sub: 'Morning, afternoon, evening' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-white/10 backdrop-blur-sm rounded-xl border border-white/10">
                    <div className="w-9 h-9 rounded-lg bg-bundle-gold/20 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-4 h-4 text-bundle-gold" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-bold">{item.label}</p>
                      <p className="text-white/60 text-xs">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== MY SAVED TRIPS ===== */}
        {myTrips.length > 0 && (
          <div className="mb-12" data-testid="my-trips-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading font-bold text-2xl text-bundle-green flex items-center gap-2">
                  <Save className="w-5 h-5" /> My Saved Trips
                </h2>
                <p className="font-body text-sm text-gray-500">{myTrips.length} trip{myTrips.length !== 1 ? 's' : ''} planned</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myTrips.map(trip => (
                <SavedTripCard
                  key={trip.trip_id}
                  trip={trip}
                  onView={() => setViewingTrip(trip)}
                  onShare={() => handleShareTrip(trip)}
                  onDelete={() => handleDeleteTrip(trip.trip_id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Quick Action Buttons */}
        <div id="book-tee-times" className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12 scroll-mt-24">
          <a 
            href="https://www.golfnow.com" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-4 border-2 border-bundle-green hover:border-bundle-green-dark hover:shadow-lg transition-all group ring-2 ring-bundle-gold/30"
          >
            <div className="w-12 h-12 bg-bundle-green/10 rounded-lg flex items-center justify-center mb-3 group-hover:bg-bundle-green/20">
              <Calendar className="w-6 h-6 text-bundle-green" />
            </div>
            <p className="font-body font-semibold text-bundle-green">Book Tee Times</p>
            <p className="font-body text-xs text-gray-500 flex items-center gap-1 mt-1">
              via GolfNow <ExternalLink className="w-3 h-3" />
            </p>
          </a>
          
          <a 
            href="https://www.google.com/travel/hotels" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-4 border border-bundle-cream-dark hover:border-bundle-green hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-bundle-gold/20 rounded-lg flex items-center justify-center mb-3 group-hover:bg-bundle-gold/30">
              <Hotel className="w-6 h-6 text-bundle-gold-dark" />
            </div>
            <p className="font-body font-semibold text-bundle-green">Find Hotels</p>
            <p className="font-body text-xs text-gray-500 flex items-center gap-1 mt-1">
              via Google Hotels <ExternalLink className="w-3 h-3" />
            </p>
          </a>
          
          <a 
            href="https://www.google.com/travel/flights" 
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white rounded-xl p-4 border border-bundle-cream-dark hover:border-bundle-green hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-blue-200">
              <Plane className="w-6 h-6 text-blue-600" />
            </div>
            <p className="font-body font-semibold text-bundle-green">Book Flights</p>
            <p className="font-body text-xs text-gray-500 flex items-center gap-1 mt-1">
              via Google Flights <ExternalLink className="w-3 h-3" />
            </p>
          </a>

          <a
            href="#rental-cars"
            onClick={(e) => { e.preventDefault(); document.getElementById('rental-cars')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="bg-white rounded-xl p-4 border border-bundle-cream-dark hover:border-bundle-green hover:shadow-lg transition-all group"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-purple-200">
              <Car className="w-6 h-6 text-purple-600" />
            </div>
            <p className="font-body font-semibold text-bundle-green">Rental Cars</p>
            <p className="font-body text-xs text-gray-500 mt-1">
              Golf-friendly options
            </p>
          </a>
          
          <button 
            onClick={() => setShowMap(!showMap)}
            className="bg-white rounded-xl p-4 border border-bundle-cream-dark hover:border-bundle-green hover:shadow-lg transition-all group text-left"
          >
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-emerald-200">
              <Globe className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="font-body font-semibold text-bundle-green">View Map</p>
            <p className="font-body text-xs text-gray-500 mt-1">
              {showMap ? 'Hide map' : 'Show destinations'}
            </p>
          </button>
        </div>

        {/* Map View */}
        {showMap && (
          <Card className="mb-12 overflow-hidden border-bundle-cream-dark">
            <CardContent className="p-0">
              <div className="bg-bundle-green/5 p-4 border-b border-bundle-cream-dark">
                <h3 className="font-heading font-bold text-bundle-green flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Golf Destinations Map
                </h3>
              </div>
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12953811.910586057!2d-111.02783815!3d35.65748824!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x54eab584e432360b%3A0x1c3bb99243deb742!2sUnited%20States!5e0!3m2!1sen!2sus!4v1650000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Golf Destinations Map"
                />
              </div>
              <div className="p-4 bg-gray-50">
                <p className="font-body text-sm text-gray-600">
                  Click on a featured destination below to view courses, book tee times, and find hotels.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bucket List Golf Trips */}
        <div className="mb-14" data-testid="bucket-list-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-bundle-gold rounded-lg flex items-center justify-center">
              <Trophy className="w-5 h-5 text-bundle-green-dark" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-bundle-green">
                Bucket List Golf Trips
              </h2>
              <p className="font-body text-sm text-gray-500">Dream trips every Bundle member should experience</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {BUCKET_LIST_TRIPS.map((trip) => (
              <div
                key={trip.id}
                data-testid={`bucket-${trip.id}`}
                className="rounded-2xl overflow-hidden bg-white border border-bundle-cream-dark hover:shadow-xl transition-shadow group"
              >
                {/* Image Header */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={trip.image}
                    alt={trip.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${trip.gradient} opacity-70`} />
                  <div className="absolute top-3 left-3">
                    <span className="bg-bundle-gold text-bundle-green-dark font-body text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <Flag className="w-3 h-3" /> Bucket List
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-heading font-bold text-lg text-white leading-tight">{trip.title}</h3>
                    <p className="font-body text-xs text-white/80 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {trip.location}
                    </p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="font-body text-sm text-gray-600 leading-relaxed mb-4">{trip.why}</p>

                  <div className="grid grid-cols-1 gap-2 mb-4">
                    <div className="bg-bundle-cream rounded-lg p-2.5">
                      <p className="font-body text-[10px] text-gray-500 uppercase tracking-wider">Best Time</p>
                      <p className="font-heading font-bold text-sm text-bundle-green">{trip.bestTime}</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {trip.highlights.slice(0, 4).map(h => (
                      <span key={h} className="font-body text-xs bg-bundle-green/5 text-bundle-green px-2 py-0.5 rounded-full">{h}</span>
                    ))}
                  </div>

                  <a
                    href={trip.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full bg-bundle-green text-white py-2.5 rounded-xl font-body text-sm font-semibold hover:bg-bundle-green-dark transition-colors"
                  >
                    Learn More <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Featured Destinations */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-bundle-green">
                Featured Golf Destinations
              </h2>
              <p className="font-body text-gray-600 mt-1">Handpicked trips for the ultimate golf experience</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURED_DESTINATIONS.map((destination) => (
              <Card 
                key={destination.id} 
                className="overflow-hidden border-bundle-cream-dark hover:shadow-xl transition-shadow cursor-pointer group"
                onClick={() => setSelectedDestination(destination)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={destination.image} 
                    alt={destination.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${destination.color} opacity-60`} />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-white" />
                      <span className="font-body text-sm text-white/90">{destination.state}</span>
                    </div>
                    <h3 className="font-heading font-bold text-2xl text-white">{destination.name}</h3>
                    <p className="font-body text-sm text-white/80">{destination.tagline}</p>
                  </div>
                </div>
                <CardContent className="p-4">
                  <p className="font-body text-sm text-gray-600 mb-4 line-clamp-2">{destination.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm mb-4">
                    <div className="flex items-center gap-1">
                      <Sun className="w-4 h-4 text-bundle-gold" />
                      <span className="font-body text-gray-600">{destination.bestSeason}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-bundle-gold" />
                      <span className="font-body text-gray-600">{destination.avgGreenFee}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {destination.courses.slice(0, 2).map((course) => (
                        <span key={course} className="bg-bundle-cream px-2 py-0.5 rounded text-xs font-body text-bundle-green">
                          {course}
                        </span>
                      ))}
                      {destination.courses.length > 2 && (
                        <span className="bg-bundle-cream px-2 py-0.5 rounded text-xs font-body text-gray-500">
                          +{destination.courses.length - 2} more
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-bundle-green group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* More Destinations */}
        <div>
          <h2 className="font-heading font-bold text-2xl text-bundle-green mb-6">
            More Golf Destinations
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {ALL_DESTINATIONS.filter(d => !FEATURED_DESTINATIONS.find(f => f.id === d.id)).map((dest) => (
              <a
                key={dest.id}
                href={dest.golfnowLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl p-4 border border-bundle-cream-dark hover:border-bundle-green hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-bundle-cream rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-bundle-green" />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-bundle-green group-hover:text-bundle-green-dark">{dest.name}</p>
                    <p className="font-body text-xs text-gray-500">{dest.state}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Rental Cars Section */}
        <div id="rental-cars" className="mt-12" data-testid="rental-cars-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl text-bundle-green">Rental Cars</h2>
              <p className="font-body text-sm text-gray-500">Room for your clubs, comfort for the crew</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                name: 'Enterprise',
                url: 'https://www.enterprise.com',
                desc: 'Wide selection of SUVs and minivans. Most locations offer golf-friendly vehicles.',
                tags: ['SUVs', 'Minivans', 'Airport Pickup'],
                color: 'bg-green-50 border-green-100',
                iconBg: 'bg-green-100',
                iconColor: 'text-green-700'
              },
              {
                name: 'Hertz',
                url: 'https://www.hertz.com',
                desc: 'Premium fleet with spacious trunks. Great for groups heading to the course.',
                tags: ['Premium Fleet', 'Spacious', 'Nationwide'],
                color: 'bg-yellow-50 border-yellow-100',
                iconBg: 'bg-yellow-100',
                iconColor: 'text-yellow-700'
              },
              {
                name: 'Avis',
                url: 'https://www.avis.com',
                desc: 'Reliable rentals at every major airport. SUVs that fit 4 golf bags easily.',
                tags: ['Airport Locations', '4-Bag SUVs', 'Rewards'],
                color: 'bg-red-50 border-red-100',
                iconBg: 'bg-red-100',
                iconColor: 'text-red-700'
              },
              {
                name: 'National',
                url: 'https://www.nationalcar.com',
                desc: 'Choose your own car from the aisle. Perfect for golfers who want control.',
                tags: ['Choose Your Car', 'Emerald Club', 'Fast Pickup'],
                color: 'bg-emerald-50 border-emerald-100',
                iconBg: 'bg-emerald-100',
                iconColor: 'text-emerald-700'
              },
              {
                name: 'Budget',
                url: 'https://www.budget.com',
                desc: 'Affordable options for golf trips. Full-size sedans and SUVs available.',
                tags: ['Budget-Friendly', 'Full-Size', 'Easy Booking'],
                color: 'bg-blue-50 border-blue-100',
                iconBg: 'bg-blue-100',
                iconColor: 'text-blue-700'
              },
              {
                name: 'Turo',
                url: 'https://www.turo.com',
                desc: 'Rent unique vehicles from local hosts. Find luxury rides for a special golf outing.',
                tags: ['Peer-to-Peer', 'Unique Cars', 'Local Hosts'],
                color: 'bg-purple-50 border-purple-100',
                iconBg: 'bg-purple-100',
                iconColor: 'text-purple-700'
              },
            ].map((company) => (
              <a
                key={company.name}
                href={company.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`rental-${company.name.toLowerCase()}`}
                className={`rounded-xl p-5 border ${company.color} hover:shadow-lg transition-all group block`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 ${company.iconBg} rounded-lg flex items-center justify-center`}>
                      <Car className={`w-5 h-5 ${company.iconColor}`} />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-bundle-green">{company.name}</h3>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-bundle-green transition-colors" />
                </div>
                <p className="font-body text-sm text-gray-600 mb-3 leading-relaxed">{company.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {company.tags.map(tag => (
                    <span key={tag} className="font-body text-xs bg-white/80 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
                  ))}
                </div>
              </a>
            ))}
          </div>

          <div className="mt-6 p-4 bg-bundle-cream rounded-xl border border-bundle-cream-dark">
            <p className="font-body text-sm text-gray-600">
              <strong className="text-bundle-green">Pro Tip:</strong> When booking for a golf trip, look for full-size SUVs or minivans — they comfortably fit 4 golf bags plus luggage. Ask about golf-friendly vehicles at pickup.
            </p>
          </div>
        </div>
      </div>

      {/* Destination Detail Modal */}
      {selectedDestination && selectedDestination.description && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedDestination(null)}>
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-56 overflow-hidden">
              <img 
                src={selectedDestination.image} 
                alt={selectedDestination.name}
                className="w-full h-full object-cover"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${selectedDestination.color} opacity-60`} />
              <button 
                onClick={() => setSelectedDestination(null)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-white/30"
              >
                ✕
              </button>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-white" />
                  <span className="font-body text-sm text-white/90">{selectedDestination.state}</span>
                </div>
                <h2 className="font-heading font-bold text-3xl text-white">{selectedDestination.name}</h2>
                <p className="font-body text-white/80">{selectedDestination.tagline}</p>
              </div>
            </div>

            <div className="p-6">
              <p className="font-body text-gray-600 mb-6">{selectedDestination.description}</p>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-bundle-cream rounded-xl p-4">
                  <p className="font-body text-sm text-gray-500">Best Season</p>
                  <p className="font-heading font-bold text-bundle-green">{selectedDestination.bestSeason}</p>
                </div>
                <div className="bg-bundle-cream rounded-xl p-4">
                  <p className="font-body text-sm text-gray-500">Avg. Green Fee</p>
                  <p className="font-heading font-bold text-bundle-green">{selectedDestination.avgGreenFee}</p>
                </div>
              </div>

              {/* Courses */}
              <div className="mb-6">
                <h3 className="font-heading font-bold text-bundle-green mb-3">Featured Courses</h3>
                <div className="space-y-2">
                  {selectedDestination.courses.map((course) => (
                    <div key={course} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-bundle-green/10 rounded-full flex items-center justify-center">
                        <Mountain className="w-4 h-4 text-bundle-green" />
                      </div>
                      <span className="font-body text-bundle-green">{course}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <a
                  href={selectedDestination.golfnowLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-bundle-green text-white py-3 px-4 rounded-xl font-body font-semibold hover:bg-bundle-green-dark transition-colors"
                >
                  <Calendar className="w-5 h-5" />
                  Book Tee Times
                </a>
                <a
                  href={selectedDestination.hotelsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-bundle-gold text-bundle-green-dark py-3 px-4 rounded-xl font-body font-semibold hover:bg-bundle-gold/90 transition-colors"
                >
                  <Hotel className="w-5 h-5" />
                  Find Hotels
                </a>
                <a
                  href={`https://www.google.com/travel/flights?q=flights+to+${selectedDestination.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 px-4 rounded-xl font-body font-semibold hover:bg-gray-200 transition-colors"
                >
                  <Plane className="w-5 h-5" />
                  Book Flights
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== AI TRIP PLANNER MODAL ===== */}
      <Dialog open={showPlanner} onOpenChange={(open) => { setShowPlanner(open); if (!open) { setGeneratedTrip(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-bundle-green flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-bundle-gold" /> AI Trip Planner
            </DialogTitle>
            <DialogDescription>
              {generatedTrip ? 'Your custom itinerary — review, save, and book.' : 'Tell us what you want and we\'ll draft the perfect golf trip.'}
            </DialogDescription>
          </DialogHeader>

          {!generatedTrip && !planning && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm text-gray-600">Where to? *</Label>
                <Input
                  data-testid="trip-destination-input"
                  placeholder="e.g. Myrtle Beach, Scotland, Cabo San Lucas"
                  value={prefs.destination}
                  onChange={e => setPrefs({ ...prefs, destination: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-gray-600">Days</Label>
                  <Input
                    type="number" min="1" max="14"
                    data-testid="trip-duration-input"
                    value={prefs.duration_days}
                    onChange={e => setPrefs({ ...prefs, duration_days: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Group Size</Label>
                  <Input
                    type="number" min="1" max="20"
                    data-testid="trip-group-input"
                    value={prefs.group_size}
                    onChange={e => setPrefs({ ...prefs, group_size: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Budget per Person (USD, optional)</Label>
                <Input
                  type="number" placeholder="e.g. 2000"
                  data-testid="trip-budget-input"
                  value={prefs.budget_per_person}
                  onChange={e => setPrefs({ ...prefs, budget_per_person: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-gray-600">Skill Level</Label>
                  <select
                    data-testid="trip-skill-select"
                    value={prefs.skill_level}
                    onChange={e => setPrefs({ ...prefs, skill_level: e.target.value })}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="scratch">Scratch</option>
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Travel Style</Label>
                  <select
                    data-testid="trip-style-select"
                    value={prefs.travel_style}
                    onChange={e => setPrefs({ ...prefs, travel_style: e.target.value })}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
                  >
                    <option value="budget">Budget</option>
                    <option value="balanced">Balanced</option>
                    <option value="luxury">Luxury</option>
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-sm text-gray-600">Special Preferences (optional)</Label>
                <textarea
                  data-testid="trip-notes-input"
                  rows={2}
                  placeholder="e.g. oceanfront only, walking courses, must include a Pete Dye design"
                  value={prefs.notes}
                  onChange={e => setPrefs({ ...prefs, notes: e.target.value })}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bundle-green"
                />
              </div>
            </div>
          )}

          {planning && (
            <div className="py-12 text-center" data-testid="trip-planning-loader">
              <Loader2 className="w-12 h-12 mx-auto text-bundle-green animate-spin mb-4" />
              <p className="font-heading font-bold text-bundle-green text-lg">Drafting your trip...</p>
              <p className="font-body text-sm text-gray-500 mt-1">Picking the best courses and hotels for {prefs.destination}</p>
            </div>
          )}

          {generatedTrip && <ItineraryView trip={generatedTrip.itinerary} prefs={generatedTrip.preferences} />}

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            {!generatedTrip && !planning && (
              <>
                <Button variant="outline" onClick={() => setShowPlanner(false)} className="sm:flex-none">Cancel</Button>
                <Button
                  data-testid="trip-generate-btn"
                  onClick={handlePlanTrip}
                  className="bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold flex-1"
                >
                  <Sparkles className="w-4 h-4 mr-2" /> Generate Trip
                </Button>
              </>
            )}
            {generatedTrip && (
              <>
                <Button variant="outline" onClick={() => setGeneratedTrip(null)} className="sm:flex-none">Try Again</Button>
                <Button
                  data-testid="trip-save-btn"
                  onClick={handleSaveTrip}
                  disabled={savingTrip}
                  className="btn-bundle flex-1"
                >
                  {savingTrip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save This Trip
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== VIEW SAVED TRIP MODAL ===== */}
      <Dialog open={!!viewingTrip} onOpenChange={(open) => { if (!open) setViewingTrip(null); }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {viewingTrip && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-bundle-green">
                  {viewingTrip.itinerary?.title || 'Trip'}
                </DialogTitle>
                <DialogDescription>
                  Saved {new Date(viewingTrip.created_at).toLocaleDateString()}
                </DialogDescription>
              </DialogHeader>
              <ItineraryView trip={viewingTrip.itinerary} prefs={viewingTrip.preferences} />
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => handleShareTrip(viewingTrip)} className="flex-1">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                <Button onClick={() => setViewingTrip(null)} className="btn-bundle flex-1">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─────────── Sub-components ─────────── */

function SavedTripCard({ trip, onView, onShare, onDelete }) {
  const itin = trip.itinerary || {};
  return (
    <div
      data-testid={`saved-trip-${trip.trip_id}`}
      className="group relative rounded-2xl bg-white border border-bundle-cream-dark hover:border-bundle-gold hover:shadow-xl transition-all overflow-hidden cursor-pointer"
      onClick={onView}
    >
      <div className="h-2 bg-gradient-to-r from-bundle-green to-bundle-gold" />
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0 flex-1">
            <p className="font-body text-[10px] uppercase tracking-widest text-bundle-gold font-bold mb-1">
              {itin.duration_days || trip.preferences?.duration_days}-day trip
            </p>
            <h3 className="font-heading font-bold text-lg text-bundle-green leading-tight line-clamp-2">
              {itin.title || trip.preferences?.destination}
            </h3>
          </div>
        </div>
        <p className="font-body text-xs text-gray-500 mb-3 line-clamp-2">{itin.summary}</p>
        <div className="flex flex-wrap gap-3 text-[11px] text-gray-600 mb-4">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-bundle-gold" />{itin.destination || trip.preferences?.destination}</span>
          {itin.estimated_cost_per_person && (
            <span className="flex items-center gap-1 text-bundle-gold-dark font-semibold"><DollarSign className="w-3 h-3" />${itin.estimated_cost_per_person}/pp</span>
          )}
          <span className="flex items-center gap-1"><Users className="w-3 h-3 text-bundle-gold" />{trip.preferences?.group_size}</span>
        </div>
        <div className="flex gap-2">
          <Button onClick={(e) => { e.stopPropagation(); onView(); }} variant="outline" size="sm" className="flex-1 text-xs">View</Button>
          <Button onClick={(e) => { e.stopPropagation(); onShare(); }} variant="outline" size="sm" className="text-xs">
            <Share2 className="w-3 h-3" />
          </Button>
          <Button onClick={(e) => { e.stopPropagation(); onDelete(); }} variant="outline" size="sm" className="text-xs text-red-500 hover:bg-red-50 hover:border-red-200">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ItineraryView({ trip, prefs }) {
  if (!trip) return null;
  // External booking helpers — these can be swapped to affiliate URLs later
  const golfNowSearch = (course) => `https://www.golfnow.com/tee-times#searchterm=${encodeURIComponent(course)}`;
  const bookingHotel = (hotel) => `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(hotel)}`;
  const flightSearch = (dest) => `https://www.google.com/travel/flights?q=${encodeURIComponent('flights to ' + dest)}`;

  return (
    <div className="space-y-5 py-2" data-testid="trip-itinerary-view">
      {/* Hero summary */}
      <div className="rounded-2xl bg-gradient-to-br from-bundle-green to-bundle-green-dark p-5 text-white">
        <p className="font-body text-[10px] uppercase tracking-widest text-bundle-gold font-bold mb-1">{trip.duration_days}-day · {prefs?.travel_style || 'balanced'}</p>
        <h3 className="font-heading font-bold text-2xl mb-2">{trip.title}</h3>
        <p className="text-white/80 text-sm mb-4">{trip.summary}</p>
        <div className="flex flex-wrap gap-2 text-xs">
          {trip.estimated_cost_per_person && (
            <span className="px-2.5 py-1 rounded-full bg-bundle-gold/20 border border-bundle-gold/40 text-bundle-gold font-bold">
              ${trip.estimated_cost_per_person} per person
            </span>
          )}
          {trip.best_season && (
            <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/20">{trip.best_season}</span>
          )}
          <span className="px-2.5 py-1 rounded-full bg-white/10 border border-white/20">{trip.destination}</span>
        </div>
      </div>

      {/* Highlights */}
      {trip.highlights?.length > 0 && (
        <div>
          <p className="font-heading font-bold text-bundle-green text-sm uppercase tracking-wider mb-2">Highlights</p>
          <ul className="space-y-1.5">
            {trip.highlights.map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <Star className="w-4 h-4 text-bundle-gold flex-shrink-0 mt-0.5" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Day-by-day itinerary */}
      {trip.days?.length > 0 && (
        <div>
          <p className="font-heading font-bold text-bundle-green text-sm uppercase tracking-wider mb-3">Day by Day</p>
          <div className="space-y-3">
            {trip.days.map((d) => (
              <div key={d.day} className="rounded-xl border border-bundle-cream-dark p-4 bg-bundle-cream/30">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-heading font-bold text-bundle-green">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-bundle-green text-white text-xs mr-2">{d.day}</span>
                    {d.title}
                  </h4>
                  {d.estimated_day_cost && (
                    <span className="text-xs font-bold text-bundle-gold-dark">${d.estimated_day_cost}/pp</span>
                  )}
                </div>
                <div className="space-y-2 text-sm">
                  {d.morning && <p><span className="font-bold text-bundle-green">Morning:</span> <span className="text-gray-700">{d.morning}</span></p>}
                  {d.afternoon && <p><span className="font-bold text-bundle-green">Afternoon:</span> <span className="text-gray-700">{d.afternoon}</span></p>}
                  {d.evening && <p><span className="font-bold text-bundle-green">Evening:</span> <span className="text-gray-700">{d.evening}</span></p>}
                </div>
                {d.course_played && (
                  <a
                    href={golfNowSearch(d.course_played)}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-bundle-green bg-bundle-gold/10 hover:bg-bundle-gold/20 px-3 py-1.5 rounded-full transition-colors"
                  >
                    <Calendar className="w-3 h-3" />Book {d.course_played} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended courses */}
      {trip.recommended_courses?.length > 0 && (
        <div>
          <p className="font-heading font-bold text-bundle-green text-sm uppercase tracking-wider mb-3">Must-Play Courses</p>
          <div className="space-y-2">
            {trip.recommended_courses.map((c, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-bundle-cream-dark">
                <div className="w-10 h-10 rounded-full bg-bundle-green/10 flex items-center justify-center flex-shrink-0">
                  <Flag className="w-4 h-4 text-bundle-green" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-sm text-bundle-green truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 line-clamp-1">{c.why}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{c.city} · ~${c.approx_green_fee} green fee</p>
                </div>
                <a
                  href={golfNowSearch(c.name)}
                  target="_blank" rel="noopener noreferrer"
                  className="flex-shrink-0 px-3 py-1.5 rounded-full bg-bundle-green text-white text-[11px] font-bold hover:bg-bundle-green-dark"
                >
                  Book
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hotel */}
      {trip.hotel_suggestion && (
        <div>
          <p className="font-heading font-bold text-bundle-green text-sm uppercase tracking-wider mb-3">Where to Stay</p>
          <div className="flex items-center gap-3 p-4 rounded-xl bg-white border border-bundle-cream-dark">
            <div className="w-12 h-12 rounded-xl bg-bundle-gold/10 flex items-center justify-center flex-shrink-0">
              <Hotel className="w-5 h-5 text-bundle-gold-dark" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-bundle-green truncate">{trip.hotel_suggestion.name}</p>
              <p className="text-xs text-gray-500">{trip.hotel_suggestion.neighborhood}</p>
              {trip.hotel_suggestion.approx_nightly && (
                <p className="text-[11px] text-gray-400 mt-0.5">~${trip.hotel_suggestion.approx_nightly}/night</p>
              )}
            </div>
            <a
              href={bookingHotel(trip.hotel_suggestion.name)}
              target="_blank" rel="noopener noreferrer"
              className="flex-shrink-0 px-3 py-1.5 rounded-full bg-blue-600 text-white text-[11px] font-bold hover:bg-blue-700 inline-flex items-center gap-1"
            >
              Book <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}

      {/* Pro tips */}
      {trip.pro_tips?.length > 0 && (
        <div className="rounded-xl bg-bundle-cream p-4">
          <p className="font-heading font-bold text-bundle-green text-sm uppercase tracking-wider mb-2">Pro Tips</p>
          <ul className="space-y-1.5">
            {trip.pro_tips.map((t, i) => (
              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                <Sparkles className="w-3.5 h-3.5 text-bundle-gold flex-shrink-0 mt-0.5" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick book row */}
      <div className="grid grid-cols-3 gap-2 pt-2">
        <a href={flightSearch(trip.destination)} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold">
          <Plane className="w-4 h-4" />Flights
        </a>
        <a href={`https://www.golfnow.com/tee-times#searchterm=${encodeURIComponent(trip.destination)}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 p-3 rounded-xl bg-bundle-green/10 hover:bg-bundle-green/20 text-bundle-green text-xs font-bold">
          <Calendar className="w-4 h-4" />Tee Times
        </a>
        <a href={`https://www.booking.com/searchresults.html?ss=${encodeURIComponent(trip.destination)}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 p-3 rounded-xl bg-bundle-gold/15 hover:bg-bundle-gold/25 text-bundle-gold-dark text-xs font-bold">
          <Hotel className="w-4 h-4" />Hotels
        </a>
      </div>
    </div>
  );
}
